import React, { useCallback, useEffect, useState } from "react";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import ReportRegistrationsFrom from "../reportRegistrations/ReportRegistrationsFrom";
import ReportManagementTable from "../reportRegistrations/ReportManagementTable";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import "../../assets/css/reportRegistrations.css";

const ReportAdministration = () => {
    const [activeTab, setActiveTab] = useState("management");
    const [searchText, setSearchText] = useState("");
    const [reports, setReports] = useState([]);
    const [isReportsLoading, setIsReportsLoading] = useState(true);
    const [reportsError, setReportsError] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editReportData, setEditReportData] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteReportData, setDeleteReportData] = useState(null);
    const [isUpdatingReport, setIsUpdatingReport] = useState(false);
    const [deletingReportId, setDeletingReportId] = useState(null);
    const [toastState, setToastState] = useState({
        show: false,
        type: "success",
        message: "",
    });

    const passtoken =
        !localStorage.getItem("access-token-storage") ||
        localStorage.getItem("access-token-storage") === null
            ? null
            : JSON.parse(localStorage.getItem("access-token-storage"))["accessToken"];

    const reportsEndpoint = `${process.env.REACT_APP_SERVER_URL}/aipd/report-management/reports`;

    const buildReportUrl = (reportId, groupId) =>
        `https://app.powerbi.com/reportEmbed?reportId=${reportId || ""}&groupId=${groupId || ""}`;

    const showToast = (message, type = "success") => {
        setToastState({
            show: true,
            type,
            message,
        });
    };

    const normalizeReportsPayload = (payload) => {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (Array.isArray(payload?.data?.reports)) {
            return payload.data.reports;
        }

        if (payload?.data && Array.isArray(payload.data)) {
            return payload.data;
        }

        if (payload?.data && typeof payload.data === "object") {
            return [payload.data];
        }

        if (Array.isArray(payload?.reports)) {
            return payload.reports;
        }

        if (payload && typeof payload === "object" && (payload.reportID || payload.reportId)) {
            return [payload];
        }

        return [];
    };

    const formatCreatedAt = (createdAt) => {
        if (createdAt === null || createdAt === undefined || createdAt === "") {
            return "-";
        }

        if (typeof createdAt === "number") {
            const date = new Date(createdAt);
            return Number.isNaN(date.getTime()) ? String(createdAt) : date.toLocaleString();
        }

        return String(createdAt);
    };

    const fetchReports = useCallback(async () => {
        setIsReportsLoading(true);
        setReportsError("");

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const response = await fetch(reportsEndpoint, {
                headers,
            });

            const json = await response.json();

            if (!response.ok) {
                throw new Error(json?.message || "Failed to load reports.");
            }

            const fetchedReports = normalizeReportsPayload(json).map((report) => ({
                reportName: report.reportName || "-",
                reportId: report.reportGuid ?? report.reportGuid ?? "-",
                apiReportId:
                    report.reportId ??
                    report.reportID ??
                    report.report_id ??
                    report.id ??
                    null,
                reportGuid: report.reportGuid || "",
                groupId: report.groupId || report.groupID || "-",
                datasetId: report.datasetId || "-",
                createdBy: report.createdBy || report.createdby || "-",
                createdAt: formatCreatedAt(report.createdAt),
                rlsEnabled: Boolean(report.rls ?? report.rlsEnabled),
                reportDescription:
                    report.reportDescriptions || report.reportDescription || "",
                reportUrl: report.reportURL || report.reportUrl || "",
            }));

            setReports(fetchedReports);
        } catch (error) {
            setReports([]);
            setReportsError(error?.message || "Unable to fetch reports at the moment.");
        } finally {
            setIsReportsLoading(false);
        }
    }, [passtoken, reportsEndpoint]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    useEffect(() => {
        if (!toastState.show) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setToastState((prevState) => ({ ...prevState, show: false }));
        }, 3000);

        return () => clearTimeout(timer);
    }, [toastState.show]);

    const handleEditReport = (report) => {
        const editableReportGuid = report.reportGuid || report.reportId;

        setEditReportData({
            originalReportId: report.reportId,
            originalApiReportId: report.apiReportId,
            reportName: report.reportName,
            reportId: editableReportGuid,
            groupId: report.groupId,
            datasetId: report.datasetId,
            rlsEnabled: String(report.rlsEnabled),
            reportDescription: report.reportDescription || "",
            reportUrl: buildReportUrl(editableReportGuid, report.groupId),
        });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditReportData(null);
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditReportData((prevData) => {
            if (!prevData) {
                return prevData;
            }

            const updatedData = { ...prevData, [name]: value };
            return {
                ...updatedData,
                reportUrl: buildReportUrl(updatedData.reportId, updatedData.groupId),
            };
        });
    };

    const handleUpdateReport = (event) => {
        event.preventDefault();

        if (!editReportData) {
            return;
        }

        const reportName = String(editReportData.reportName || "").trim();
        const reportGuid = String(editReportData.reportId || "").trim();
        const groupId = String(editReportData.groupId || "").trim();
        const datasetId = String(editReportData.datasetId || "").trim();
        const reportDescription = String(editReportData.reportDescription || "").trim();

        if (!reportName || !reportGuid || !groupId || !datasetId || !reportDescription) {
            showToast("All input fields are required.", "danger");
            return;
        }

        const requestPayload = {
            reportName,
            reportGuid,
            groupID: groupId,
            datasetId,
            rls: editReportData.rlsEnabled === "true",
            reportURL: buildReportUrl(reportGuid, groupId),
            reportDescriptions: reportDescription,
        };

        const updateReport = async () => {
            setIsUpdatingReport(true);

            try {
                const headers = {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                };

                if (passtoken) {
                    headers["oidc-token"] = passtoken;
                }

                const headerReportId =
                    editReportData.originalApiReportId ?? editReportData.originalReportId;
                const requestReportId =
                    headerReportId ?? editReportData.originalReportId;

                if (headerReportId !== undefined && headerReportId !== null) {
                    headers["reportId"] = String(headerReportId);
                }

                const response = await fetch(
                    `${reportsEndpoint}/${requestReportId}`,
                    {
                        method: "PUT",
                        headers,
                        body: JSON.stringify(requestPayload),
                    }
                );

                const responseJson = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(
                        responseJson?.message || "Failed to update report."
                    );
                }

                setReports((prevReports) =>
                    prevReports.map((report) =>
                        report.reportId === editReportData.originalReportId
                            ? {
                                  ...report,
                                  reportName,
                                  reportGuid,
                                  groupId,
                                  datasetId,
                                  rlsEnabled: editReportData.rlsEnabled === "true",
                                  reportDescription,
                                  reportUrl: requestPayload.reportURL,
                              }
                            : report
                    )
                );

                showToast("Report updated successfully.", "success");
                handleCloseEditModal();
            } catch (error) {
                showToast(
                    error?.message || "Unable to update report at the moment.",
                    "danger"
                );
            } finally {
                setIsUpdatingReport(false);
            }
        };

        updateReport();
    };

    const handleOpenDeleteModal = (report) => {
        if (!report || deletingReportId !== null) {
            return;
        }

        setDeleteReportData({
            reportId: report.reportId,
            apiReportId: report.apiReportId,
            reportName: report.reportName,
        });
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        if (deletingReportId !== null) {
            return;
        }

        setShowDeleteModal(false);
        setDeleteReportData(null);
    };

    const handleDeleteReport = async () => {
        const reportId = deleteReportData?.reportId;

        if (!reportId || deletingReportId !== null) {
            return;
        }

        setDeletingReportId(reportId);

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const headerReportId = deleteReportData?.apiReportId ?? reportId;
            const requestReportId = headerReportId ?? reportId;
            headers["reportId"] = String(headerReportId);

            const response = await fetch(`${reportsEndpoint}/${requestReportId}`, {
                method: "DELETE",
                headers,
            });

            if (response.status === 204) {
                setReports((prevReports) =>
                    prevReports.filter((report) => report.reportId !== reportId)
                );
                showToast("Report deleted successfully.", "success");
                setShowDeleteModal(false);
                setDeleteReportData(null);
                return;
            }

            if (response.status === 404) {
                throw new Error("Report not found.");
            }

            const responseJson = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(responseJson?.message || "Failed to delete report.");
            }

            setReports((prevReports) =>
                prevReports.filter((report) => report.reportId !== reportId)
            );
            showToast("Report deleted successfully.", "success");
            setShowDeleteModal(false);
            setDeleteReportData(null);
        } catch (error) {
            showToast(
                error?.message || "Unable to delete report at the moment.",
                "danger"
            );
        } finally {
            setDeletingReportId(null);
        }
    };

    const filteredReports = reports.filter((report) => {
        const query = searchText.toLowerCase();
        return (
            String(report.reportName || "")
                .toLowerCase()
                .includes(query) ||
            String(report.reportId || "")
                .toLowerCase()
                .includes(query) ||
            String(report.groupId || "")
                .toLowerCase()
                .includes(query)
        );
    });

    return (
        <div>
            {toastState.show && (
                <div
                    style={{
                        position: "fixed",
                        top: "16px",
                        right: "16px",
                        zIndex: 99999,
                        minWidth: "320px",
                        maxWidth: "420px",
                    }}
                >
                    <div
                        className={`alert alert-${toastState.type} shadow-sm mb-0`}
                        role="alert"
                    >
                        {toastState.message}
                    </div>
                </div>
            )}

            <NavHeader />
            <SideMenu />
            <div style={{ position: "relative" }} id="main">
                <div className="container-fluid pt-3 pb-4">
                    <h1 className="mb-1">Report Administration</h1>
                    <p className="text-muted mb-0">
                        Manage and register Power BI reports, including metadata,
                        dataset mapping, and RLS settings.
                    </p>
                </div>

                <div className="container-fluid pb-4">
                    <div className="rr-tabsWrap">
                        <button
                            type="button"
                            onClick={() => setActiveTab("management")}
                            className={`rr-tabBtn ${
                                activeTab === "management" ? "active" : ""
                            }`}
                        >
                            Report Management
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("registrations")}
                            className={`rr-tabBtn ${
                                activeTab === "registrations" ? "active" : ""
                            }`}
                        >
                            Report Registrations
                        </button>
                    </div>
                </div>

                {activeTab === "management" ? (
                    <ReportManagementTable
                        searchText={searchText}
                        setSearchText={setSearchText}
                        filteredReports={filteredReports}
                        isLoading={isReportsLoading}
                        errorMessage={reportsError}
                        deletingReportId={deletingReportId}
                        onEditReport={handleEditReport}
                        onDeleteReport={handleOpenDeleteModal}
                    />
                ) : (
                    <ReportRegistrationsFrom />
                )}

                <Dialog
                    open={Boolean(showEditModal && editReportData)}
                    onClose={handleCloseEditModal}
                    fullWidth
                    maxWidth="lg"
                    PaperProps={{ className: "rr-manageCard rr-editDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">Edit Report</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseEditModal}
                        ></button>
                    </DialogTitle>

                    <form onSubmit={handleUpdateReport}>
                        <DialogContent className="pt-3">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label rr-label">Report Name</label>
                                    <input
                                        type="text"
                                        name="reportName"
                                        className="form-control rr-input"
                                        value={editReportData?.reportName || ""}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label rr-label">Report ID</label>
                                    <input
                                        type="text"
                                        name="reportId"
                                        className="form-control rr-input"
                                        value={editReportData?.reportId || ""}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label rr-label">Group ID</label>
                                    <input
                                        type="text"
                                        name="groupId"
                                        className="form-control rr-input"
                                        value={editReportData?.groupId || ""}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label rr-label">Dataset ID</label>
                                    <input
                                        type="text"
                                        name="datasetId"
                                        className="form-control rr-input"
                                        value={editReportData?.datasetId || ""}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label rr-label">Report URL</label>
                                    <input
                                        type="text"
                                        name="reportUrl"
                                        className="form-control rr-input"
                                        value={editReportData?.reportUrl || ""}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label rr-label">RLS Enabled</label>
                                    <div
                                        className="rr-toggle"
                                        role="radiogroup"
                                        aria-label="RLS Enabled"
                                    >
                                        <label
                                            className={`rr-toggleOption ${
                                                editReportData?.rlsEnabled === "true"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="rlsEnabled"
                                                value="true"
                                                checked={editReportData?.rlsEnabled === "true"}
                                                onChange={handleEditChange}
                                            />
                                            True
                                        </label>
                                        <label
                                            className={`rr-toggleOption ${
                                                editReportData?.rlsEnabled === "false"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="rlsEnabled"
                                                value="false"
                                                checked={editReportData?.rlsEnabled === "false"}
                                                onChange={handleEditChange}
                                            />
                                            False
                                        </label>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <label className="form-label rr-label">
                                        Report Description
                                    </label>
                                    <textarea
                                        name="reportDescription"
                                        className="form-control rr-input rr-textarea"
                                        rows="4"
                                        value={editReportData?.reportDescription || ""}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </div>
                            </div>
                        </DialogContent>

                        <DialogActions className="border-0 pt-2">
                            <button
                                type="button"
                                className="btn rr-btnGhost rr-deleteModalActionBtn"
                                onClick={handleCloseEditModal}
                                disabled={isUpdatingReport}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn rr-btnPrimary rr-deleteModalActionBtn"
                                disabled={isUpdatingReport}
                            >
                                {isUpdatingReport ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Updating...
                                    </>
                                ) : (
                                    "Update"
                                )}
                            </button>
                        </DialogActions>
                    </form>
                </Dialog>

                <Dialog
                    open={Boolean(showDeleteModal && deleteReportData)}
                    onClose={handleCloseDeleteModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ className: "rr-manageCard rr-deleteDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">Confirm Deletion</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseDeleteModal}
                            disabled={deletingReportId !== null}
                        ></button>
                    </DialogTitle>

                    <DialogContent className="pt-3">
                        <p className="mb-0 rr-deleteModalText">
                            Are you sure you want to delete report
                            {" "}
                            <strong>{deleteReportData?.reportName || "this report"}</strong>
                            ? This action cannot be undone.
                        </p>
                    </DialogContent>

                    <DialogActions className="border-0 pt-2">
                        <button
                            type="button"
                            className="btn rr-btnGhost rr-deleteModalActionBtn"
                            onClick={handleCloseDeleteModal}
                            disabled={deletingReportId !== null}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn rr-btnPrimary rr-deleteModalActionBtn"
                            onClick={handleDeleteReport}
                            disabled={deletingReportId !== null}
                        >
                            {String(deletingReportId) === String(deleteReportData?.reportId) ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

export default ReportAdministration;
